/**
 * File hotspots and the risk summary.
 *
 * This is where the demo's planted findings surface as something a visitor can
 * point at: `payments-service` is owned by one person, churns hard, and is the
 * repo the org would most regret losing. Our Staff/Principal persona arrives
 * looking for exactly that (bus factor, silos), and our VP persona needs it as
 * the "what am I not seeing" moment.
 *
 * Hotspots are derived from the per-repo profile rather than hand-listed, so a
 * repo whose profile is tuned later cannot end up with a risk story that
 * contradicts its own metrics.
 */

import type {
  OrgFileHotspot,
  OrgFileHotspotsResponse,
  RiskSignal,
  RiskSummary,
} from '@/types/insights';
import {
  DEMO_ORG_ID,
  DEMO_REPOS,
  findDemoDeveloper,
  findDemoRepo,
  findDemoTeam,
  type DemoRepo,
} from '../fixture';
import { demoDate, jitter, randInt, rngFor, type DemoSeedInput } from '../seed';

export interface DemoInsightsParams {
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
}

const seedInput = (params: DemoInsightsParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.repoId,
  teamId: params.teamId,
  excludeBots: params.excludeBots,
});

/**
 * High-traffic paths, keyed by REPOSITORY rather than by language.
 *
 * Keyed by language first, which put the identical TypeScript file list under
 * both `web-app` and `shared-libs` - so `src/components/CheckoutForm.tsx`
 * appeared as a hotspot in a shared utility library. Spotted on the live page;
 * a prospect scanning the table would spot it too.
 */
const FILES_BY_REPO: Record<string, string[]> = {
  'web-app': [
    'src/components/CheckoutForm.tsx',
    'src/lib/apiClient.ts',
    'src/hooks/useSession.ts',
    'src/store/cartStore.ts',
    'src/components/DataTable.tsx',
    'src/routes/checkout.tsx',
  ],
  'payments-service': [
    'internal/ledger/posting.go',
    'internal/gateway/stripe.go',
    'internal/refund/service.go',
    'internal/ledger/reconcile.go',
    'cmd/worker/main.go',
  ],
  'orders-api': [
    'app/api/orders.py',
    'app/services/fulfilment.py',
    'app/models/order.py',
    'app/tasks/dispatch.py',
    'app/api/webhooks.py',
  ],
  'platform-infra': [
    'modules/network/main.tf',
    'environments/prod/cluster.tf',
    'modules/iam/policies.tf',
    'environments/prod/rds.tf',
  ],
  'shared-libs': [
    'packages/dates/format.ts',
    'packages/errors/codes.ts',
    'packages/http/retry.ts',
  ],
};

function hotspotsForRepo(
  repo: DemoRepo,
  params: DemoInsightsParams
): OrgFileHotspot[] {
  const { profile, repository } = repo;
  const rng = rngFor(seedInput(params, 'insights/hotspots'), repository.id);
  const files = FILES_BY_REPO[repository.name] ?? [];

  // Derived from the repo's OWN volume, not a flat constant. A flat baseline
  // gave shared-libs - which merges 0.7 PRs a day - a single file touched by
  // 193 PRs, i.e. three quarters of everything the repo ever merged. The
  // busiest file gets ~22% of the repo's PRs, which is high but arguable.
  const repoPrs = profile.prsPerDay * params.days * 0.92;

  return files.map((filename, index) => {
    // Rank decay: the first file in a repo is the real hotspot, the rest tail
    // off. A flat distribution would make the page look like noise.
    const rank = 1 / (index + 1.4);
    const changeCount = Math.max(
      2,
      Math.round(repoPrs * 0.31 * rank * profile.churnMultiplier * jitter(rng, 0.2))
    );

    // Contributor concentration IS the finding. A repo whose profile says one
    // person owns 82% of it must show files with one or two contributors.
    //
    // The floor of 2 for everything else is load-bearing, not tidiness: the
    // unbounded randInt(1, 5) handed single-owner files to web-app - the
    // healthy flagship - which then rendered as HIGH bus-factor risk. That
    // erases the very contrast the fixture exists to draw, and it showed up on
    // the live page as an alarming finding in the repo we hold up as fine.
    const concentrated = profile.topContributorShare > 0.7;
    const available = profile.contributorIds.length;
    const uniqueContributors = concentrated
      ? index < 3
        ? 1
        : Math.min(2, available)
      : randInt(rng, Math.min(2, available), Math.max(Math.min(2, available), Math.min(available, 5)));

    const contributorIds = profile.contributorIds.slice(0, uniqueContributors);
    const previousChangeCount = Math.max(1, Math.round(changeCount / jitter(rng, 0.3)));
    const trendPercentage = Number(
      (((changeCount - previousChangeCount) / previousChangeCount) * 100).toFixed(1)
    );

    return {
      filename,
      repository_id: repository.id,
      repository_name: repository.name,
      change_count: changeCount,
      total_lines_changed: Math.round(changeCount * profile.avgPrSize * 0.42 * jitter(rng, 0.2)),
      unique_contributors: uniqueContributors,
      contributor_ids: contributorIds,
      contributor_logins: contributorIds.map((id) => findDemoDeveloper(id)?.login ?? id),
      risk_level:
        // Share-based, not an absolute count. A fixed ">20 changes" threshold
        // was tuned to the old flat baseline and stopped firing once change
        // counts became proportional to each repo's own volume - so the
        // planted bus-factor finding silently disappeared from the page.
        uniqueContributors === 1 && changeCount >= Math.max(4, repoPrs * 0.1)
          ? 'high'
          : uniqueContributors <= 2 || profile.churnMultiplier > 1.4
            ? 'medium'
            : 'low',
      trend_direction: Math.abs(trendPercentage) < 8 ? 'stable' : trendPercentage > 0 ? 'up' : 'down',
      trend_percentage: trendPercentage,
      previous_change_count: previousChangeCount,
      weekly_changes: Array.from({ length: 8 }, () =>
        Math.max(0, Math.round((changeCount / 8) * jitter(rng, 0.45)))
      ),
    } satisfies OrgFileHotspot;
  });
}

function reposInScope(params: DemoInsightsParams): DemoRepo[] {
  const scoped = findDemoRepo(params.repoId);
  if (scoped) return [scoped];

  const team = findDemoTeam(params.teamId);
  if (team) return DEMO_REPOS.filter((r) => team.repoIds.includes(r.repository.id));

  return DEMO_REPOS;
}

export function buildDemoFileHotspots(params: DemoInsightsParams): OrgFileHotspotsResponse {
  const repos = reposInScope(params);
  const hotspots = repos
    .flatMap((repo) => hotspotsForRepo(repo, params))
    .sort((a, b) => b.change_count - a.change_count);

  return {
    organization_id: DEMO_ORG_ID,
    time_period: `${params.days}d`,
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    hotspots,
    total_files_analyzed: repos.length * 340,
  };
}

export function buildDemoRiskSummary(params: DemoInsightsParams): RiskSummary {
  const repos = reposInScope(params);
  const rng = rngFor(seedInput(params, 'insights/risk'), 'signals');
  const signals: RiskSignal[] = [];

  for (const { repository, profile } of repos) {
    const owner = findDemoDeveloper(profile.contributorIds[0]);

    // Bus factor. Severity tracks the concentration in the profile, so this
    // cannot drift away from what the hotspots and contributor lists show.
    if (profile.topContributorShare >= 0.55) {
      signals.push({
        signal_type: 'bus_factor',
        severity: profile.topContributorShare >= 0.7 ? 'critical' : 'warning',
        entity_type: 'repository',
        entity_id: repository.id,
        entity_name: repository.name,
        value: Number((profile.topContributorShare * 100).toFixed(1)),
        metadata: {
          sole_owner_login: owner?.login ?? null,
          sole_owner_name: owner?.name ?? null,
          contributors: profile.contributorIds.length,
        },
      });
    }

    // Review queue: idle waiting dominating cycle time is a different problem
    // from slow review, and the distinction is the point of the page.
    if (profile.waitForReviewShare >= 0.45) {
      signals.push({
        signal_type: 'review_bottleneck',
        severity: profile.waitForReviewShare >= 0.5 ? 'critical' : 'warning',
        entity_type: 'repository',
        entity_id: repository.id,
        entity_name: repository.name,
        value: Number((profile.cycleTimeHours * profile.waitForReviewShare).toFixed(1)),
        metadata: {
          share_of_cycle_time: Number((profile.waitForReviewShare * 100).toFixed(1)),
          cycle_time_hours: profile.cycleTimeHours,
        },
      });
    }

    if (profile.churnMultiplier >= 1.6) {
      signals.push({
        signal_type: 'rework',
        severity: 'warning',
        entity_type: 'repository',
        entity_id: repository.id,
        entity_name: repository.name,
        value: Number((profile.churnMultiplier * 100 - 100).toFixed(1)),
        metadata: { baseline: 'org median' },
      });
    }
  }

  // Workload concentration on the review hubs. Framed developmentally (load to
  // redistribute), never as a performance judgement - our product philosophy
  // is explicit that individual metrics are for debugging, not evaluation.
  if (repos.length > 1) {
    for (const id of ['dev-priya', 'dev-marcus']) {
      const dev = findDemoDeveloper(id);
      if (!dev) continue;
      signals.push({
        signal_type: 'review_load_concentration',
        severity: id === 'dev-priya' ? 'critical' : 'warning',
        entity_type: 'developer',
        entity_id: dev.id,
        entity_name: dev.name,
        value: Number((dev.reviewGiveWeight * jitter(rng, 0.05)).toFixed(1)),
        metadata: { share_of_reviews: `${dev.reviewGiveWeight}%` },
      });
    }
  }

  return {
    critical_count: signals.filter((s) => s.severity === 'critical').length,
    warning_count: signals.filter((s) => s.severity === 'warning').length,
    signals,
  };
}
